import { useEffect, useState } from 'react'
import { DatePicker } from 'antd';
import CommonSelect from '../../../../core/common/commonSelect';
import { isValidPhoneNumber, isValidEmail, toDateFromEpoch, isNotEmpty } from '../../../../common/Utils';
import dayjs from 'dayjs';
import { API, GENDER } from '../../../../common/Constants'
import useFetch from '../../../../hooks/useFetch';
import PreLoader from '../../../../core/common/preLoader';
import { getAPIHeaders } from '../../../../common/Utils';
import FormFooter from '../../../../core/common/formFooter';
import ProfilePic from '../../../../core/common/profilePic';
import CustomModal from '../../../../core/common/customModal';

type PasswordField = "password" | "confirmPassword";

interface Props {
  user: any;
  onSuccess: () => void;
  onClose: () => void;
  roleOpts: any[];
  userRoles?: any[];
}

const UserEditModal = ({ user, onSuccess, onClose, roleOpts, userRoles }: Props) => {
  const [formUser, setFormUser] = useState<any>({});
  const [msgBox, setMsgBox] = useState<any>({
    msg: "",
    error: false
  });
  const { isLoading, error, data, status, callFetch } = useFetch();
  const [defaultGender, setDefaultGender] = useState<any>();
  const [activeTab, setActiveTab] = useState<'basic' | 'account' | 'access'>('basic');

  useEffect(() => {
    setFormUser((prev: any) => ({
      ...prev,
      roles: userRoles || []
    }));
  }, [userRoles]);

  useEffect(() => {
    if (user) {
      // Split userFullName into firstName and lastName
      const nameParts = user.userFullName ? user.userFullName.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormUser((prev: any) => ({
        ...user,
        firstName,
        lastName,
        password: '',
        confirmPassword: '',
        roles: prev?.roles || []
      }));

      setDefaultGender({
        ...GENDER.find((ele) => ele.value === user?.gender)
      });
    }
  }, [user]);

  const resetMsgBox = () => {
    setMsgBox({
      msg: "",
      error: false
    });
  }

  const getModalContainer = () => {
    const modalElement = document.getElementById('modal-datepicker');
    return modalElement ? modalElement : document.body;
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  useEffect(() => {
    if (data && status && status < 299) {
      setMsgBox({ msg: "Updated successfully", error: false });
      onSuccess();
    }
  }, [data, status]);

  useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setMsgBox({ msg: error.response?.data, error: true });
      } else {
        setMsgBox({ msg: 'Something went wrong please try again later.', error: true });
      }
    }
  }, [error]);

  const handleBasicInfoSubmit = (event: any) => {
    event.preventDefault();
    setMsgBox({ msg: "", error: false });

    if (!isNotEmpty(formUser?.firstName)) {
      setMsgBox({ msg: "First Name is required.", error: true });
      return;
    }

    if (!isNotEmpty(formUser?.lastName)) {
      setMsgBox({ msg: "Last Name is required.", error: true });
      return;
    }

    if (!isValidEmail(formUser?.email)) {
      setMsgBox({ msg: "Email is invalid.", error: true });
      return;
    }

    if (!formUser?.dob) {
      setMsgBox({ msg: "Date of Birth is required.", error: true });
      return;
    }

    if (!isNotEmpty(formUser?.gender)) {
      setMsgBox({ msg: "Gender is required.", error: true });
      return;
    }

    if (!isValidPhoneNumber(formUser?.phoneNumber)) {
      setMsgBox({ msg: "Please provide valid 10 digit phone number", error: true });
      return;
    }

    // Proceed with API call if validation passes
    callFetch(
      `${API.HRM_PROFILE}/${formUser?.userAccountId}`,
      'PUT',
      getAPIHeaders(),
      null,
      {
        firstName: formUser?.firstName,
        lastName: formUser?.lastName,
        phoneNumber: formUser?.phoneNumber,
        email: formUser?.email,
        dob: formUser?.dob,
        gender: formUser?.gender,
      }
    );
  };

  const handlePasswordSubmit = (event: any) => {
    event.preventDefault();
    if (formUser?.password !== formUser?.confirmPassword) {
      setMsgBox({ msg: "Password mismatch! Password and confirm password must match", error: true });
    } else {
      setMsgBox({ msg: "", error: false });
      callFetch(API.PWD_RESET,
        'POST',
        getAPIHeaders(),
        null,
        {
          accountId: formUser?.userAccountId,
          newPassword: formUser?.password,
        }
      );
    }
  }

  const handleSubmitRoles = (event: any) => {
    event.preventDefault();
    setMsgBox({ msg: "", error: false });
    if (formUser?.userAccountId) {
      callFetch(API.USER_ROLES.replace('{id}', `${formUser?.userAccountId}`),
        'POST',
        getAPIHeaders(),
        null,
        [...formUser?.roles?.map((role: any) => {
          return role.value
        }) || []]
      );
    }
  }

  const renderProfilePicForm = () => {
    return (
      <ProfilePic id={formUser?.userAccountId} />
    );
  }

  const renderBasicInfoForm = () => {
    return (
      <div className="modal-body pb-0">
        <div className="row">
          <div className="col-md-12">
            {renderProfilePicForm()}
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                First Name <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formUser?.firstName || ''}
                onChange={(e) => {
                  setFormUser({
                    ...formUser,
                    firstName: e?.target?.value
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Last Name <span className="text-danger"> *</span></label>
              <input
                type="text"
                className="form-control"
                value={formUser?.lastName || ''}
                onChange={(e) => {
                  setFormUser({
                    ...formUser,
                    lastName: e?.target?.value
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Date of Birth <span className="text-danger"> *</span>
              </label>
              <div className="input-icon-end position-relative">
                <DatePicker
                  className="form-control datetimepicker"
                  format={{
                    format: "DD-MM-YYYY",
                    type: "mask",
                  }}
                  getPopupContainer={getModalContainer}
                  placeholder="DD-MM-YYYY"
                  value={formUser?.dob ? dayjs(toDateFromEpoch(formUser?.dob)) : null}
                  onChange={(dt) => {
                    setFormUser({
                      ...formUser,
                      dob: dt?.toDate()?.getTime() / 1000
                    });
                  }}
                />
                <span className="input-icon-addon">
                  <i className="ti ti-calendar text-gray-7" />
                </span>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Gender <span className="text-danger"> *</span>
              </label>
              <CommonSelect
                className='select'
                options={GENDER}
                defaultValue={defaultGender}
                onChange={(value) => {
                  setFormUser({
                    ...formUser,
                    gender: value
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Email <span className="text-danger"> *</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={formUser?.email || ''}
                onChange={(e) => {
                  setFormUser({
                    ...formUser,
                    email: e?.target?.value
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Phone Number <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formUser?.phoneNumber || ''}
                onChange={(e) => {
                  setFormUser({
                    ...formUser,
                    phoneNumber: parseInt(e?.target?.value)
                  });
                }}
                onKeyPress={(e) => {
                  // Allow only numeric input
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderAccountForm = () => {
    return (
      <div className="modal-body pb-0">
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Username <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formUser?.username || ''}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Password <span className="text-danger"> *</span>
              </label>
              <div className="pass-group">
                <input
                  type={
                    passwordVisibility.password
                      ? "text"
                      : "password"
                  }
                  className="pass-input form-control"
                  value={formUser?.password || ""}
                  onChange={(e) => {
                    setFormUser({
                      ...formUser,
                      password: e?.target?.value
                    });
                  }}
                />
                <span
                  className={`ti toggle-passwords ${passwordVisibility.password
                    ? "ti-eye"
                    : "ti-eye-off"
                    }`}
                  onClick={() =>
                    togglePasswordVisibility("password")
                  }
                ></span>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Confirm Password <span className="text-danger"> *</span>
              </label>
              <div className="pass-group">
                <input
                  type={
                    passwordVisibility.confirmPassword
                      ? "text"
                      : "password"
                  }
                  className="pass-input form-control"
                  value={formUser?.confirmPassword || ""}
                  onChange={(e) => {
                    setFormUser({
                      ...formUser,
                      confirmPassword: e?.target?.value
                    });
                  }}
                />
                <span
                  className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                    ? "ti-eye"
                    : "ti-eye-off"
                    }`}
                  onClick={() =>
                    togglePasswordVisibility("confirmPassword")
                  }
                ></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasAdminRole = () => {
    if (!formUser?.roles || !roleOpts || !roleOpts.length) return false;
    const adminRole = roleOpts.find((role: any) => role.label === 'ADMIN');
    if (!adminRole) return false;
    return formUser.roles.some((role: any) => role.value === adminRole.value);
  };

  const handleTesterToggle = (checked: boolean) => {
    if (!roleOpts || !roleOpts.length) return;
    
    const adminRole = roleOpts.find((role: any) => role.label === 'ADMIN');
    if (!adminRole) {
      setMsgBox({ msg: "ADMIN role not found in available roles.", error: true });
      return;
    }

    let updatedRoles = [...(formUser?.roles || [])];
    
    if (checked) {
      // Add ADMIN role if not already present
      const hasAdmin = updatedRoles.some((role: any) => role.value === adminRole.value);
      if (!hasAdmin) {
        updatedRoles.push({
          label: adminRole.label,
          value: adminRole.value
        });
      }
    } else {
      // Remove ADMIN role
      updatedRoles = updatedRoles.filter((role: any) => role.value !== adminRole.value);
    }

    setFormUser({
      ...formUser,
      roles: updatedRoles
    });
  };

  const renderAccessForm = () => {
    const isTesterEnabled = hasAdminRole();

    return (
      <div className="modal-body pb-0">
        <div className="row">
          <div className="col-md-8">
            <div className="mb-3">
              <label className="form-label">
                Tester <span className="text-danger"> *</span>
              </label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="testerToggle"
                  checked={isTesterEnabled}
                  onChange={(e) => handleTesterToggle(e.target.checked)}
                  style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                />
                <label className="form-check-label ms-2" htmlFor="testerToggle">
                  Enable tester mode (Allows adding subscriptions without payment)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfoForm();
      case 'account':
        return renderAccountForm();
      case 'access':
        return renderAccessForm();
      default:
        return renderBasicInfoForm();
    }
  }

  const renderFooter = () => {
    const getSaveHandler = () => {
      switch (activeTab) {
        case 'basic':
          return handleBasicInfoSubmit;
        case 'account':
          return handlePasswordSubmit;
        case 'access':
          return handleSubmitRoles;
        default:
          return handleBasicInfoSubmit;
      }
    };

    return (
      <FormFooter
        onCancel={onClose}
        onSave={getSaveHandler()}
        error={msgBox.error}
        message={msgBox.msg}
      />
    );
  }

  return (
    <CustomModal
      show={!!user}
      title={`Edit User - ${user?.userFullName || ''}`}
      onClose={onClose}
      size="lg"
      bodyContent={
        <div>
          <PreLoader showLoader={isLoading} />
          
          {/* Tabs */}
          <div className="contact-grids-tab">
            <ul className="nav nav-underline" id="myTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('basic');
                    resetMsgBox();
                  }}
                >
                  Basic Information
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('account');
                    resetMsgBox();
                  }}
                >
                  Account Info
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'access' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('access');
                    resetMsgBox();
                  }}
                >
                  Access Info
                </button>
              </li>
            </ul>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {renderTabContent()}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            {renderFooter()}
          </div>
        </div>
      }
    />
  );
}

export default UserEditModal;
